/** Small colour helpers: the console derives its whole accent scale from one green. */

export type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b]
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** Linear mix of two colours; `amount` 0 = a, 1 = b. */
export function mix(a: Rgb, b: Rgb, amount: number): Rgb {
  return [0, 1, 2].map((i) => (a[i] ?? 0) + ((b[i] ?? 0) - (a[i] ?? 0)) * amount) as Rgb;
}

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

export interface AccentScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

/** A 50–900 scale around `base` (treated as the 600 step). */
export function accentScale(base: Rgb): AccentScale {
  const tint = (t: number) => toHex(mix(base, WHITE, t));
  const shade = (t: number) => toHex(mix(base, BLACK, t));
  return {
    50: tint(0.94),
    100: tint(0.86),
    200: tint(0.72),
    300: tint(0.52),
    400: tint(0.3),
    500: tint(0.14),
    600: toHex(base),
    700: shade(0.16),
    800: shade(0.32),
    900: shade(0.5),
  };
}

/** The platform console's accent: PackPilot green (#05473E), the same as the shop app's default brand. */
export const CONSOLE_ACCENT = '#05473E';

function toHsl([r, g, b]: Rgb): [number, number, number] {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h * 60, s, l];
}

function fromHsl([h, s, l]: [number, number, number]): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

/**
 * The accent for dark mode: the scale's 400 step when it reads on `surface` (4.5:1), otherwise the
 * brand's own hue lifted in lightness until it does. Very dark brands (deep green, navy) then stay
 * recognisably theirs instead of turning grey.
 */
export function darkModeAccent(
  base: Rgb,
  surface: Rgb,
): { main: string; light: string; dark: string } {
  const scale = accentScale(base);
  const step400 = parseHex(scale[400]);
  if (step400 && contrastRatio(step400, surface) >= 4.5) {
    return { main: scale[400], light: scale[300], dark: scale[500] };
  }
  const [h, s0] = toHsl(base);
  const s = Math.min(s0, 0.62);
  let l = 0.3;
  while (l < 0.85 && contrastRatio(fromHsl([h, s, l]), surface) < 4.6) l += 0.01;
  return {
    main: toHex(fromHsl([h, s, l])),
    light: toHex(fromHsl([h, s, Math.min(l + 0.14, 0.92)])),
    dark: toHex(fromHsl([h, s, Math.max(l - 0.1, 0.2)])),
  };
}
